import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField
} from '@mui/material';
import { FC, ReactElement, useContext, useEffect, useState } from 'react';
import { OrgContext } from './OrgContext';

import { OrganizationUpdateInput } from '@/API';
import { updateOrganization } from '@/client/organization';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { CloseIcon } from '../Icons/CommonIcons';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import config from '@/config';

type Inputs = {
  image?: string;
  name: string;
  numAdmin: string;
  numHost: string;
  status: string;
  subdomain: string;
  domains: string;
  onFreeTrial: string;
  freeTrialExpiration: Dayjs | null;
};

const EditOrgModal: FC = (): ReactElement => {
  const { isEdit, setIsEdit, editFocus, showModal, setShowModal, currentOrg, setQueryOrgs } =
    useContext(OrgContext);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const form = useForm<Inputs>({
    defaultValues: {
      name: currentOrg?.name || '',
      subdomain: currentOrg?.subdomain || '',
      onFreeTrial: currentOrg?.onFreeTrial?.toString() || 'false',
      freeTrialExpiration: currentOrg?.freeTrialExpiration
        ? dayjs(currentOrg.freeTrialExpiration)
        : null
    }
  });

  const { register, handleSubmit, formState, reset, watch, control, setError, setValue } = form;
  const { errors } = formState;

  const watchOnFreeTrial = watch('onFreeTrial');
  const watchSubdomain = watch('subdomain');

  const resetForm = () => {
    setShowModal(false);
    if (isEdit) {
      setIsEdit(false);
    }
    reset();
  };

  useEffect(() => {
    if (currentOrg) {
      reset({
        name: currentOrg.name || '',
        subdomain: currentOrg.subdomain || '',
        onFreeTrial: currentOrg.onFreeTrial?.toString() || 'false',
        freeTrialExpiration: currentOrg.freeTrialExpiration
          ? dayjs(currentOrg.freeTrialExpiration)
          : null
      });
    }
  }, [showModal, currentOrg, reset]);

  const updateOrg = async (org: OrganizationUpdateInput) => {
    const response = await updateOrganization(org);
    return response;
  };

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    // Format the date if it's a Dayjs object
    let freeTrialExpiration = null;
    if (data.onFreeTrial.toString() === 'true' && data.freeTrialExpiration) {
      freeTrialExpiration = data.freeTrialExpiration.toISOString();
    }

    const orgApiData: OrganizationUpdateInput = {
      id: currentOrg.id,
      name: data.name,
      subdomain: data.subdomain,
      adminLicenses: parseInt(data.numAdmin),
      hostLicenses: parseInt(data.numHost),
      organizationActive: data.status.toString() === 'true',
      emailDomains: data.domains.split(',').map((domain) => domain.trim()),
      onFreeTrial: data.onFreeTrial.toString() === 'true',
      freeTrialExpiration
    };

    setIsSubmitting(true);

    updateOrg(orgApiData)
      .then(() => {
        setQueryOrgs(true);
        resetForm();
      })
      .catch((err: any) => {
        const message =
          err?.errors?.[0]?.message || err?.message || 'Unable to update organization';
        if (/name already exists/i.test(message)) {
          setError('name', { message });
        } else if (/URL already exists/i.test(message)) {
          setError('subdomain', { message });
        } else {
          setError('name', { message });
        }
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <Dialog open={showModal} onClose={resetForm} maxWidth={'md'} fullWidth={true}>
      <DialogTitle>
        <div className="text-[24px] font-bold mb-[16px]">
          Edit Organization
          <IconButton
            aria-label="close"
            onClick={resetForm}
            sx={{
              position: 'absolute',
              right: 28,
              top: 28
            }}
          >
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent className="scrollContainer">
        {currentOrg && (
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <DialogContentText className="text-black">Organization Name</DialogContentText>
            <TextField
              className="mb-[24px]"
              margin="dense"
              id="orgName"
              type="text"
              fullWidth
              variant="outlined"
              placeholder="Enter a name for this organization"
              defaultValue={currentOrg?.name}
              error={!!errors.name}
              helperText={errors.name?.message}
              {...register('name', {
                required: 'Organization name is required'
              })}
            />
            <div className="flex justify-between gap-4 mb-[36px]">
              <span className="w-[400px]">
                <DialogContentText className="text-black">
                  Number of Admin Licenses
                </DialogContentText>
                <TextField
                  className="mb-[8px]"
                  margin="dense"
                  id="1"
                  type="text"
                  fullWidth
                  autoFocus={editFocus === 'admin'}
                  variant="outlined"
                  placeholder="Enter a number"
                  defaultValue={currentOrg?.adminLicenses}
                  {...register('numAdmin', { required: 'Number of licenses required' })}
                  error={!!errors.numAdmin}
                  helperText={errors.numAdmin?.message}
                />
              </span>
              <span className="w-[400px]">
                <DialogContentText className="text-black">
                  Number of Host Licenses
                </DialogContentText>
                <TextField
                  className="mb-[8px]"
                  margin="dense"
                  id="2"
                  type="text"
                  fullWidth
                  autoFocus={editFocus === 'host'}
                  variant="outlined"
                  placeholder="Enter a number"
                  defaultValue={currentOrg?.hostLicenses}
                  {...register('numHost', { required: 'Number of licenses required' })}
                  error={!!errors.numHost}
                  helperText={errors.numHost?.message}
                />
              </span>
            </div>
            <div className="flex justify-between gap-4 mb-[32px]">
              <FormControl className="w-[400px]">
                <FormLabel id="demo-row-radio-buttons-group-label" className="text-black">
                  Organization Status
                </FormLabel>
                <RadioGroup
                  row
                  aria-labelledby="demo-row-radio-buttons-group-label"
                  defaultValue={currentOrg?.organizationActive ? 'true' : 'false'}
                  className="h-56 my-8"
                >
                  <FormControlLabel
                    value="true"
                    control={
                      <Radio
                        sx={{
                          '&.Mui-checked': { color: '#F58C29' },
                          '& .MuiSvgIcon-root': {
                            fontSize: 32
                          }
                        }}
                      />
                    }
                    label="Active"
                    {...register('status')}
                  />
                  <FormControlLabel
                    value="false"
                    control={
                      <Radio
                        sx={{
                          '&.Mui-checked': { color: '#F58C29' },
                          '& .MuiSvgIcon-root': {
                            fontSize: 32
                          }
                        }}
                      />
                    }
                    label="Paused"
                    {...register('status')}
                  />
                </RadioGroup>
              </FormControl>
              <span className="w-[400px]">
                <DialogContentText className="text-black">Organization URL</DialogContentText>
                <div className="flex">
                  <TextField
                    className="w-[280px]"
                    margin="dense"
                    id="orgSubdomain"
                    type="text"
                    fullWidth
                    variant="outlined"
                    placeholder="Organization Name"
                    defaultValue={currentOrg?.subdomain}
                    error={!!errors.subdomain}
                    helperText={errors.subdomain?.message}
                    {...register('subdomain', {
                      required: 'Organization URL is required',
                      onChange: (e) => {
                        const sanitized = e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '');
                        setValue('subdomain', sanitized, { shouldValidate: true });
                      }
                    })}
                  />
                  <div className="flex items-center ml-[6px]">
                    <span>.{config.siteDomain}</span>
                  </div>
                </div>
                {watchSubdomain && watchSubdomain !== currentOrg?.subdomain && (
                  <DialogContentText className="text-black text-xs ml-14 mr-120 mb-8">
                    Please note that users need to use the new URL to access the tool. Past URLs
                    won’t be redirecting to new URL.
                  </DialogContentText>
                )}
              </span>
            </div>
            <div className="flex justify-between mb-[36px]">
              <span className="w-[360px]">
                <FormLabel id="demo-row-radio-buttons-group-label" className="text-black">
                  Free Trial
                </FormLabel>
                <RadioGroup
                  row
                  aria-labelledby="demo-row-radio-buttons-group-label"
                  defaultValue={currentOrg?.onFreeTrial.toString()}
                  className="h-56 my-8"
                >
                  <FormControlLabel
                    value={'true'}
                    control={
                      <Radio
                        sx={{
                          '&.Mui-checked': { color: '#F58C29' },
                          '& .MuiSvgIcon-root': {
                            fontSize: 32
                          }
                        }}
                      />
                    }
                    label="On"
                    {...register('onFreeTrial')}
                  />
                  <FormControlLabel
                    value={'false'}
                    control={
                      <Radio
                        sx={{
                          '&.Mui-checked': { color: '#F58C29' },
                          '& .MuiSvgIcon-root': {
                            fontSize: 32
                          }
                        }}
                      />
                    }
                    label="Off"
                    {...register('onFreeTrial')}
                  />
                </RadioGroup>
              </span>
              {watchOnFreeTrial === 'true' && (
                <span className="w-[400px]">
                  <DialogContentText className="text-black">
                    Free Trial Expiration
                  </DialogContentText>
                  <Controller
                    name="freeTrialExpiration"
                    control={control}
                    rules={{
                      validate: (value) =>
                        (watchOnFreeTrial === 'true' && !!value) || 'Free trial expiration required'
                    }}
                    render={({ field }) => (
                      <DesktopDatePicker
                        {...field}
                        className="mb-[8px]"
                        slotProps={{
                          textField: {
                            margin: 'dense',
                            fullWidth: true,
                            variant: 'outlined',
                            error: !!formState.errors.freeTrialExpiration,
                            helperText: formState.errors.freeTrialExpiration?.message
                          }
                        }}
                      />
                    )}
                  />
                </span>
              )}
            </div>
            <div className="mb-[36px]">
              <DialogContentText className="text-black">Available Email Domains</DialogContentText>
              <TextField
                margin="dense"
                id="5"
                type="text"
                fullWidth
                autoFocus={editFocus === 'domains'}
                variant="outlined"
                placeholder="Enter email domains (e.g., example.com, test.org)"
                defaultValue={currentOrg?.emailDomains?.join(', ')}
                {...register('domains', {
                  required: 'At least one email domain is required',
                  validate: (value) => {
                    const domains = value.split(/[,\s]+/).filter(Boolean);
                    if (domains.length === 0) {
                      return 'At least one email domain is required';
                    }
                    const domainRegex =
                      /^(?!:\/\/)([a-zA-Z0-9-_]+\.)*[a-zA-Z0-9][a-zA-Z0-9-_]+\.[a-zA-Z]{2,11}?$/;
                    const invalidDomains = domains.filter((domain) => !domainRegex.test(domain));
                    if (invalidDomains.length > 0) {
                      return `Invalid domain(s): ${invalidDomains.join(', ')}. Use format like "example.com" or "sub.example.co.uk"`;
                    }
                    return true;
                  }
                })}
                error={!!errors.domains}
                helperText={errors.domains?.message}
              />
            </div>
          </form>
        )}
      </DialogContent>
      <DialogActions>
        <button className="btn btn-neutral secondary mr-[5px]" onClick={resetForm}>
          Cancel
        </button>
        <button
          className="btn btn-primary"
          type="submit"
          onClick={handleSubmit(onSubmit)}
          disabled={isSubmitting}
        >
          {isSubmitting ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Save'}
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default EditOrgModal;
